import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DialogDataWithSelect } from '@core/services/dialog.service';
import { IdTitleSelector } from '@core/models/ui.models';
import { of, ReplaySubject } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, startWith, switchMap, takeUntil, tap } from 'rxjs/operators';

/** пауза на набор перед запросом к серверу */
const SERVER_SEARCH_DEBOUNCE_MS = 300;

/**
 * @description диалоговое алерт окно с инпутом в виде селектора, используется в связке с DialogService,
 * настраиваемые параметры передаются через MatDialogRef/MAT_DIALOG_DATA
 * при подтверждении эмитит значение селектора в dialogRef, при отмене null
 * @param title заголовок окна, по умолчанию "Подтвердите действие"
 * @param content дополнительная инфо более мелким шрифтом
 * @param placeholder placeholder для инпута
 * @param items массив данных для селектора
 * @param searchFn поиск на сервере вместо items: окно само запрашивает список по введённой строке
 * @param confirmBtn текст на кнопке подтверждения, по умолчанию "Добавить"
 * @param cancelBtn текст на кнопке подтверждения, по умолчанию "Отмена"
 * @param isError красит текст content (дополнительная инфо) в красный цвет
 * @param validators добавляет функции-валидаторы для инпута
 */

@Component({
    selector: 'app-alert-with-select',
    templateUrl: './alert-with-select.component.html',
    styleUrls: ['./alert-with-select.component.less']
})
export class AlertWithSelectComponent implements OnInit, OnDestroy {
    title = 'common.confirm_action';
    content = '';
    placeholder = '';
    confirmBtn = 'common.add';
    cancelBtn = 'common.cancel';
    isError = false;
    items: IdTitleSelector[] = [];
    filteredItems: IdTitleSelector[] = [];
    itemSize = 24;
    serverSearch = false;
    /** идёт запрос списка — поле поиска показывает это вместо «ничего не найдено» */
    loading = false;

    searchString = new UntypedFormControl('', []);
    selectFC = new UntypedFormControl(null, []);

    private readonly destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);

    constructor(
        private dialogRef: MatDialogRef<AlertWithSelectComponent, string>,
        @Inject(MAT_DIALOG_DATA) private data: DialogDataWithSelect
    ) {}

    ngOnInit() {
        if (this.data) {
            this.title = this.data?.title ?? 'common.confirm_action';
            this.content = this.data?.content ?? '';
            this.placeholder = this.data?.placeholder ?? '';
            this.confirmBtn = this.data?.confirmBtn ?? 'common.add';
            this.cancelBtn = this.data?.cancelBtn ?? 'common.cancel';
            this.isError = !!this.data?.isError;
            this.items = this.data?.items ?? [];
            this.filteredItems = this.items.slice(0);
            this.serverSearch = !!this.data?.searchFn;
        }
        if (this.data.validators) {
            this.selectFC.setValidators(this.data.validators);
        }
        if (this.serverSearch) {
            this.subscribeServerSearch();
        } else {
            this.searchString.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe(() => this.searchItems());
        }
    }

    ngOnDestroy() {
        this.selectFC.setValue(null);
        this.destroyed$.next(true);
        this.destroyed$.complete();
    }

    onConfirm(): void {
        if (this.selectFC.valid) {
            this.dialogRef.close(this.selectFC.value);
        } else {
            this.selectFC.markAsDirty();
        }
    }

    onClose(): void {
        this.dialogRef.close(null);
    }

    private subscribeServerSearch() {
        this.searchString.valueChanges
            .pipe(
                startWith(''),
                debounceTime(SERVER_SEARCH_DEBOUNCE_MS),
                distinctUntilChanged(),
                tap(() => (this.loading = true)),
                switchMap((query) => this.data.searchFn((query ?? '').trim()).pipe(catchError(() => of([])))),
                takeUntil(this.destroyed$)
            )
            .subscribe((found) => {
                // селект хранит сам объект пункта: выбранный обязан остаться в списке тем же объектом,
                // иначе поле селекта потеряет подпись
                const selected = this.selectFC.value;
                const items = (found ?? []).map((item) => (item.id === selected?.id ? selected : item));
                this.items = selected && !items.includes(selected) ? [selected, ...items] : items;
                this.filteredItems = this.items;
                this.loading = false;
            });
    }

    private searchItems() {
        if (!this.items.length) {
            return;
        }
        const search = this.searchString.value?.toLowerCase();
        if (!search) {
            this.filteredItems = this.items.slice(0);
        } else {
            this.filteredItems = this.items.filter((item) => item?.title?.toLowerCase()?.includes(search));
        }
    }
}
